/**
 * SecoesV2 — blocos da proposta comercial na pagina do orcamento (Orcamento v2).
 *
 * Fiel ao mockup "Orcamento":
 *   - NotasDestaque: banners informativos no topo (notas_destaque).
 *   - TotaisOrcamento: subtotal, desconto %, valor total, toggle NF/tributos.
 *   - CondicoesComerciais: prazo de entrega + forma de pagamento (com chips).
 *   - ListasProposta: observacoes gerais numeradas + itens nao inclusos.
 *
 * Persistencia: save-on-blur via onPatch(campos) -> PATCH /api/orcamentos/{id}.
 * CSS: classes .orc2 (styles/canonical/orcamento-v2.css).
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiInfo, FiPlus, FiX } from "react-icons/fi";

import { useAuth } from "../../../context/AuthContext";
import { getPrecoOrcamento } from "../../../services/motorPreco";
import { fmtMoeda } from "../helpers";

const CHIPS_PRAZO = ["60 dias úteis", "60-90 dias", "45 dias"];
const CHIPS_PAGAMENTO = [
  {
    rotulo: "50/25/25",
    texto:
      "50% de entrada, 25% no início das montagens e 25% na montagem final.",
  },
  {
    rotulo: "70/15/15",
    texto:
      "70% de entrada, 15% no início das montagens e 15% na montagem final.",
  },
  {
    rotulo: "Cartao 6x",
    texto: "Entrada de 50% em conjunto a saldo em até 6x sem juros no cartão.",
  },
];

// ---------------------------------------------------------------
// Lista de strings editavel (compartilhada por notas/observacoes/nao-inclusos)
// ---------------------------------------------------------------

function useListaEditavel(valorInicial, salvar) {
  const [itens, setItens] = useState(valorInicial || []);
  useEffect(() => {
    setItens(valorInicial || []);
  }, [valorInicial]);

  const setItem = (i, texto) =>
    setItens((prev) => prev.map((t, idx) => (idx === i ? texto : t)));
  const persistir = () => salvar(itens.map((t) => t.trim()).filter(Boolean));
  const remover = (i) => {
    const next = itens.filter((_, idx) => idx !== i);
    setItens(next);
    salvar(next.map((t) => t.trim()).filter(Boolean));
  };
  const adicionar = (texto = "") => setItens((prev) => [...prev, texto]);

  return { itens, setItem, persistir, remover, adicionar };
}

// ---------------------------------------------------------------
// Notas em destaque (banners azuis no topo)
// ---------------------------------------------------------------

export function NotasDestaque({ orcamento, editavel, onPatch }) {
  const lista = useListaEditavel(orcamento.notas_destaque, (notas) =>
    onPatch({ notas_destaque: notas }),
  );

  if (!editavel && lista.itens.length === 0) return null;

  return (
    <div className="notes-banner">
      {lista.itens.map((nota, i) => (
        <div className="nb-item" key={i}>
          <FiInfo size={14} aria-hidden="true" />
          {editavel ? (
            <>
              <input
                className="cell-input"
                value={nota}
                onChange={(e) => lista.setItem(i, e.target.value)}
                onBlur={lista.persistir}
                placeholder="Nota exibida ao cliente..."
                aria-label={`Nota ${i + 1}`}
              />
              <button
                type="button"
                className="mini-btn danger"
                onClick={() => lista.remover(i)}
                aria-label="Remover nota"
              >
                <FiX size={12} aria-hidden="true" />
              </button>
            </>
          ) : (
            <span>{nota}</span>
          )}
        </div>
      ))}
      {editavel && (
        <button
          type="button"
          className="mini-btn"
          onClick={() => lista.adicionar()}
          style={{ alignSelf: "flex-start" }}
        >
          <FiPlus size={12} aria-hidden="true" /> Adicionar nota
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Totais / desconto / NF
// ---------------------------------------------------------------

export function TotaisOrcamento({ orcamento, editavel, onPatch }) {
  const o = orcamento;
  const { request } = useAuth();
  const [desconto, setDesconto] = useState(String(o.desconto_percentual ?? "0"));
  useEffect(() => {
    setDesconto(String(o.desconto_percentual ?? "0"));
  }, [o.desconto_percentual]);

  // Enquanto o vendedor nao define o preco final, os cards de ambiente ja
  // mostram o PROJETADO (fatia do piso). O rodape le a mesma fonte para nao
  // contradizer o que esta logo acima — somar so `preco_alocado` daria zero.
  const [preco, setPreco] = useState(null);
  const carregarPreco = useCallback(async () => {
    if (o.motor_preco !== "DIVISOR") return;
    try {
      setPreco(await getPrecoOrcamento(request, o.id, { silentError: true }));
    } catch {
      setPreco(null);
    }
  }, [request, o.id, o.motor_preco]);

  useEffect(() => {
    carregarPreco();
  }, [carregarPreco, o.updated_at]);

  useEffect(() => {
    if (o.motor_preco !== "DIVISOR") return undefined;
    const handler = () => carregarPreco();
    window.addEventListener("orc2:preco-invalidado", handler);
    return () => window.removeEventListener("orc2:preco-invalidado", handler);
  }, [carregarPreco, o.motor_preco]);

  // ADR-0050: no motor DIVISOR os campos da cascata (valor_apos_rt,
  // valor_total_final, valor_com_tributos) valem zero — o custo do item CAD
  // mora em `custo_calculado`, nao em `valor_unitario`. O total vem de
  // `valor_total`, que a property do model ja despacha por motor.
  const ehDivisor = o.motor_preco === "DIVISOR";
  // Preco definido manda; sem ele, o piso projetado (mesma regra dos cards).
  const totalDivisor = (() => {
    const definido = Number(o.valor_total || 0);
    if (definido > 0) return definido;
    const moveis = (preco?.moveis || []).reduce(
      (acc, m) => acc + Number(m.preco_alocado ?? m.preco_projetado ?? 0),
      0,
    );
    const adicionais = (preco?.adicionais || []).reduce(
      (acc, a) => acc + Number(a.valor || 0),
      0,
    );
    return moveis + adicionais;
  })();
  const precoAindaNaoDefinido = ehDivisor
    && !(Number(o.valor_total || 0) > 0)
    && totalDivisor > 0;

  const subtotal = ehDivisor
    ? totalDivisor
    : Number(o.valor_apos_rt || 0) + Number(o.valor_manual_total || 0);
  const totalFinal = ehDivisor ? totalDivisor : Number(o.valor_total_final || 0);
  const valorDesconto = ehDivisor ? 0 : subtotal - totalFinal;
  const comTributos = Number(o.valor_com_tributos || 0);
  const tributos = ehDivisor ? 0 : comTributos - totalFinal;

  return (
    <div className="orc-totals">
      <div className="tot-row">
        <span className="lbl">Subtotal dos ambientes</span>
        <span className="val mono">{fmtMoeda(subtotal)}</span>
      </div>
      {/* Desconto so existe na cascata legada. No motor DIVISOR quem classifica
          o abatimento e a faixa de alcada sobre o piso — um campo de "%" aqui
          daria duas verdades sobre o mesmo numero. */}
      {!ehDivisor && (
        <div className="tot-row discount">
          <span className="lbl">
            Desconto concedido{" "}
            <input
              className="cell-input mono"
              style={{ width: 64, textAlign: "right" }}
              value={desconto}
              onChange={(e) => setDesconto(e.target.value)}
              onBlur={() => onPatch({ desconto_percentual: desconto || "0" })}
              disabled={!editavel}
              inputMode="decimal"
              aria-label="Desconto (%)"
            />{" "}
            %
          </span>
          <span className="val mono">
            {valorDesconto > 0 ? `- ${fmtMoeda(valorDesconto)}` : fmtMoeda(0)}
          </span>
        </div>
      )}
      <div className="tot-row grand">
        <span className="lbl">
          {precoAindaNaoDefinido ? "Valor total (piso — preço não definido)" : "Valor total"}
        </span>
        <span className="val mono">{fmtMoeda(totalFinal)}</span>
      </div>
      <div className="tot-row tributos">
        <span className="lbl">
          <button
            type="button"
            className={"switch" + (o.emitir_nf ? " is-on" : "")}
            role="switch"
            aria-checked={Boolean(o.emitir_nf)}
            aria-label="Emitir NF"
            disabled={!editavel}
            onClick={() => onPatch({ emitir_nf: !o.emitir_nf })}
          />{" "}
          {ehDivisor
            ? "Emitir NF — imposto entra no cálculo do preço"
            : `Emitir NF — tributos (${Number(o.tributos_percentual || 0)}%)`}
        </span>
        {/* No DIVISOR o imposto e componente de `s` e ja esta DENTRO do preco;
            somar por cima cobraria duas vezes. */}
        <span className="val mono">
          {ehDivisor
            ? (o.emitir_nf ? "incluído no preço" : "—")
            : fmtMoeda(tributos)}
        </span>
      </div>
      {!ehDivisor && o.emitir_nf && (
        <div className="tot-row">
          <span className="lbl">Valor total com tributos</span>
          <span className="val mono">{fmtMoeda(comTributos)}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Condições comerciais (prazo + pagamento com presets)
// ---------------------------------------------------------------

// `seletorFormaPagamento` (Tarefa F2): node opcional que SUBSTITUI o campo
// texto livre — no motor DIVISOR o Detalhe injeta o select de condicao
// cadastrada e o texto da proposta passa a vir da descricao da condicao
// (sincronizado pelo backend em `escolher_condicao_pagamento`).
export function CondicoesComerciais({ orcamento, editavel, onPatch, seletorFormaPagamento }) {
  const [prazo, setPrazo] = useState(orcamento.prazo_entrega || "");
  const [pagamento, setPagamento] = useState(orcamento.forma_pagamento || "");
  useEffect(() => {
    setPrazo(orcamento.prazo_entrega || "");
  }, [orcamento.prazo_entrega]);
  useEffect(() => {
    setPagamento(orcamento.forma_pagamento || "");
  }, [orcamento.forma_pagamento]);

  return (
    <div className="block">
      <div className="block__head">
        <div className="block__title">Condicoes comerciais</div>
      </div>
      <div className="block__body">
        <div className="cond-grid">
          <div className="cond-field">
            <label>
              Prazo de entrega
              <input
                className="input"
                value={prazo}
                onChange={(e) => setPrazo(e.target.value)}
                onBlur={() => onPatch({ prazo_entrega: prazo })}
                disabled={!editavel}
                placeholder="Ex.: 60 dias úteis"
              />
            </label>
            {editavel && (
              <div className="chip-presets">
                {CHIPS_PRAZO.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="chip-preset"
                    onClick={() => {
                      setPrazo(c);
                      onPatch({ prazo_entrega: c });
                    }}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
            <p className="hint">
              A variação de prazo poderá ser combinada conforme complexidade e
              tamanho do projeto, a partir da data de pagamento da entrada.
            </p>
          </div>
          <div className="cond-field">
            {/* No motor DIVISOR a condicao cadastrada e a UNICA fonte da
                forma de pagamento (o texto da proposta vem da descricao
                dela, sincronizado pelo backend). O campo livre so existe
                nos orcamentos do motor antigo, que nao tem o select. */}
            {seletorFormaPagamento || (
              <>
                <label>
                  Forma de pagamento
                  <textarea
                    className="textarea"
                    rows={4}
                    value={pagamento}
                    onChange={(e) => setPagamento(e.target.value)}
                    onBlur={() => onPatch({ forma_pagamento: pagamento })}
                    disabled={!editavel}
                    placeholder="Ex.: 50% de entrada, 25% no início das montagens..."
                  />
                </label>
                {editavel && (
                  <div className="chip-presets">
                    {CHIPS_PAGAMENTO.map((c) => (
                      <button
                        key={c.rotulo}
                        type="button"
                        className="chip-preset"
                        onClick={() => {
                          setPagamento(c.texto);
                          onPatch({ forma_pagamento: c.texto });
                        }}
                      >
                        {c.rotulo}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Observacoes gerais (numeradas) + Itens nao inclusos
// ---------------------------------------------------------------

export function ListasProposta({ orcamento, editavel, onPatch }) {
  const obs = useListaEditavel(orcamento.observacoes_gerais, (lista) =>
    onPatch({ observacoes_gerais: lista }),
  );
  const excl = useListaEditavel(orcamento.itens_nao_inclusos, (lista) =>
    onPatch({ itens_nao_inclusos: lista }),
  );

  return (
    <div className="block">
      <div className="block__head">
        <div className="block__title">Observações gerais</div>
        {editavel && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => obs.adicionar()}
          >
            <FiPlus size={12} aria-hidden="true" /> Adicionar
          </button>
        )}
      </div>
      <div className="block__body">
        {obs.itens.length === 0 && (
          <p className="muted-mono">Nenhuma observação geral.</p>
        )}
        <ol className="obs-list">
          {obs.itens.map((texto, i) => (
            <li key={i}>
              {editavel ? (
                <>
                  <input
                    className="obs-input"
                    value={texto}
                    onChange={(e) => obs.setItem(i, e.target.value)}
                    onBlur={obs.persistir}
                    placeholder="Observação exibida na proposta..."
                    aria-label={`Observação ${i + 1}`}
                  />
                  <button
                    type="button"
                    className="obs-del"
                    onClick={() => obs.remover(i)}
                    aria-label="Remover observação"
                  >
                    <FiX size={12} aria-hidden="true" />
                  </button>
                </>
              ) : (
                <span>{texto}</span>
              )}
            </li>
          ))}
        </ol>

        <div className="excl-head">
          <span className="excl-head__t">Itens não inclusos</span>
          {editavel && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => excl.adicionar()}
            >
              <FiPlus size={12} aria-hidden="true" /> Adicionar
            </button>
          )}
        </div>
        {excl.itens.length === 0 && (
          <p className="muted-mono">Nenhum item listado.</p>
        )}
        <ul className="excl-list">
          {excl.itens.map((texto, i) => (
            <li key={i}>
              {editavel ? (
                <>
                  <input
                    className="obs-input"
                    value={texto}
                    onChange={(e) => excl.setItem(i, e.target.value)}
                    onBlur={excl.persistir}
                    placeholder="Item não incluso..."
                    aria-label={`Item nao incluso ${i + 1}`}
                  />
                  <button
                    type="button"
                    className="obs-del"
                    onClick={() => excl.remover(i)}
                    aria-label="Remover item"
                  >
                    <FiX size={12} aria-hidden="true" />
                  </button>
                </>
              ) : (
                <span>{texto}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
